/* eslint-disable max-len */
import { AsyncCommandSender } from '../core/interfaces/sender/AsyncCommandSender'
import { TypedAsyncCommandApi } from './TypedAsyncCommandApi'
import { AsyncExecutableCmd } from '../core/interfaces/command/AsyncExecutableCmd'

/**
 * Utility type to extract the error type(s) form an AsyncSender
 * see https://stackoverflow.com/questions/44851268/typescript-how-to-extract-the-generic-parameter-from-a-type
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type extractErrorFromGeneric<Type> = Type extends TypedAsyncCommandApi<any, any, infer E> ? E : never
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type extractApiFromGeneric<Type> = Type extends TypedAsyncCommandApi<infer T, any, any> ? T : never

/**
 * Utility type to extract the value of a promise
 * credit: https://stackoverflow.com/questions/48011353/how-to-unwrap-type-of-a-promise?rq=1
*/
type Await<T> = T extends {
  then(onfulfilled?: (value: infer U) => unknown): unknown;
} ? U : T;

export type Cmd<T, K extends keyof T, E> = AsyncExecutableCmd<T, K, E>
export type Res<T, K extends keyof T, E> = Await<ReturnType<Cmd<T, K, E>['exec']>>

type BeforeFn<T, K extends keyof T, E> = (cmd: Cmd<T, K, E>) => () => unknown | void
type AfterFn<T, K extends keyof T, E> = (res: Res<T, K, E>) => () => unknown | void
type OnErrorFn<E> = (error: E) => () => unknown | void
type OnSuccessFn<T> = (success: T) => () => unknown | void

function toArray<T>(i: T | T[]): T[] {
  if (i instanceof Array) return i
  return [i]
}

// type BeforeFn = (cmd: AsyncExecutableCmd<T, K, E>) => () => unknown
/* type BeforeFns = [BeforeFn] | null

type AfterFn = (res: ClientFlowCmdResult<TCmd>) => () => unknown
type AfterFns = [AfterFn] | null
 */

/**
 * Abstract base command sender class.
 * Use this class internally to build the library's senders
 * from (e.g. 'JsonRpcSender').
 */
export class TypedAsyncCommandHandler<
  A extends TypedAsyncCommandApi<T, AsyncCommandSender<unknown, E>, unknown>,
  T = extractApiFromGeneric<A>,
  E = extractErrorFromGeneric<A>
  > {
  readonly beforeFns: BeforeFn<T, keyof T, E>[] | [] | null = null

  readonly afterFns: AfterFn<T, keyof T, E>[] | [] | null = null

  readonly onErrorFns: OnErrorFn<E>[] | [] | null = null

  readonly onSuccessFns: OnSuccessFn<unknown>[] | [] | null = null

  // accepts single functions, arrays of functions or empty array
  constructor(arg?: {
    before?: BeforeFn<T, keyof T, E> | BeforeFn<T, keyof T, E>[];
    after?: AfterFn<T, keyof T, E> | AfterFn<T, keyof T, E>[];
    onError?: OnErrorFn<E> | OnErrorFn<E>[];
    onSuccess?: OnSuccessFn<unknown> | OnSuccessFn<unknown>[];
   }) {
    if (arg?.before) { this.beforeFns = toArray(arg.before) }
    if (arg?.after) { this.afterFns = toArray(arg.after) }
    if (arg?.onError) { this.onErrorFns = toArray(arg.onError) }
    if (arg?.onSuccess) { this.onSuccessFns = toArray(arg.onSuccess) }
  }

  public async handle<K extends keyof T>(cmd: Cmd<T, K, E>) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    await this.runBefore(cmd)
    const res = await cmd.exec()
    if (res.isErr()) {
      await this.runOnError(res.error)
    } else {
      await this.runOnSuccess(res.value)
    }
    await this.runAfter(res)
    return res
  }

  private async runBefore<K extends keyof T>(cmd: Cmd<T, K, E>) {
    if (this.beforeFns === null) return true
    // inject cmd argument into each 'outer' function, e.g.: (cmd) => () => {}
    const beforeFns = (this.beforeFns as Array<BeforeFn<T, K, E>>).map(
      (fn) => fn(cmd),
    )
    await Promise.all(beforeFns.map((fn) => fn())) // unpack then 'inner' function
    return true
  }

  private async runAfter<K extends keyof T>(res: Res<T, K, E>) {
    if (this.afterFns === null) return true
    // inject res argument into each 'outer' function, e.g.: (res) => () => {}
    const afterFns = (this.afterFns as Array<AfterFn<T, K, E>>).map(
      (fn) => fn(res),
    )
    await Promise.all(afterFns.map((fn) => fn())) // unpack then 'inner' function
    return true
  }

  private async runOnSuccess(val: unknown) {
    if (this.onSuccessFns === null) return true
    // inject val argument into each 'outer' function, e.g.: (val) => () => {}
    const onSuccessFns = (this.onSuccessFns as Array<OnSuccessFn<unknown>>).map(
      (fn) => fn(val),
    )
    await Promise.all(onSuccessFns.map((fn) => fn())) // unpack then 'inner' function
    return true
  }

  private async runOnError(e: E) {
    if (this.onErrorFns === null) return true
    // inject e argument into each 'outer' function, e.g.: (e) => () => {}
    const onErrorFns = (this.onErrorFns as Array<OnErrorFn<E>>).map(
      (fn) => fn(e),
    )
    await Promise.all(onErrorFns.map((fn) => fn())) // unpack then 'inner' function
    return true
  }
}
